import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthShell, TextInput, toast } from '@etherisc/ui-kit';
import { PrimaryButton } from '../../components/buttons';
import { apiClient } from '../../services/api';
import { CheckCircleIcon } from 'lucide-react';
import { MyUbuntuLogo } from '../../assets/MyUbuntuLogo';

const setPasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  
  const token = searchParams.get('token');
  const verified = searchParams.get('verified') === 'true';
  const message = searchParams.get('message');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
  });

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    // For now, assume token is valid if we got here from email verification
    // In a more robust implementation, we could validate the token with the backend
    setTokenValid(true);
  }, [token]);

  const handleSetPassword = async (data: SetPasswordFormData) => {
    if (!token) {
      toast.error('Invalid token', {
        description: 'The verification token is missing or invalid.',
      });
      return;
    }

    try {
      setLoading(true);
      
      await apiClient.setPassword(token, data.password);
      
      // If we get here without an error, the password was set successfully
      // API client unwraps the response, so we don't need to check response.success
      toast.success('Password set successfully!', {
        description: 'You can now login with your new password.',
      });
      
      // Delay to ensure toast is visible before redirect
      setTimeout(() => {
        navigate('/login?success=password_set&message=' + encodeURIComponent('Password set successfully. You can now login.'));
      }, 1500);
    } catch (error: unknown) {
      console.error('Set password error:', error);
      toast.error('Failed to set password', {
        description: error instanceof Error ? error.message : 'Please try again or contact support.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Validating verification token...</p>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (tokenValid === false || !token) {
    return (
      <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-900">Invalid Verification Token</h2>
            <p className="mt-2 text-sm text-gray-600">
              The verification token is invalid or has expired.
            </p>
            <div className="mt-6">
              <PrimaryButton
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Go to Login
              </PrimaryButton>
            </div>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Set Your Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Complete your account setup by creating a secure password
          </p>
        </div>

        {/* Email Verification Success Message */}
        {verified && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800">Email Verified Successfully</p>
                {message && (
                  <p className="text-sm text-green-600 mt-1">{decodeURIComponent(message)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(handleSetPassword)} className="space-y-6">
          <div>
            <TextInput
              id="password"
              label="New Password"
              type="password"
              {...register('password')}
              error={errors.password?.message}
              placeholder="Enter your new password"
            />
          </div>

          <div>
            <TextInput
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
              placeholder="Confirm your new password"
            />
          </div>

          {/* Password Requirements */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Password Requirements:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• At least 8 characters long</li>
              <li>• Include at least one uppercase letter</li>
              <li>• Include at least one lowercase letter</li>
              <li>• Include at least one number</li>
              <li>• Include at least one special character</li>
            </ul>
          </div>

          <div>
            <PrimaryButton
              type="submit"
              disabled={loading || isSubmitting}
              className="w-full"
            >
              {loading || isSubmitting ? 'Setting Password...' : 'Set Password & Login'}
            </PrimaryButton>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Need help?</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
